import React, { useState, useEffect } from 'react';
import { Menu, X, PawPrint, Stethoscope, Store, Home } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      // Track which section is currently in view
      const sections = ['home', 'about', 'petshop', 'emergency'];
      for (const id of sections.reverse()) {
        const el = document.getElementById(id);
        if (el && window.scrollY >= el.offsetTop - 100) {
          setActiveSection(id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  const navLinks = [
    { name: 'Home',          id: 'home',           icon: <Home size={17} className="mr-1.5" /> },
    { name: 'How It Works',  id: 'how-it-works',   icon: null },
    { name: 'About',         id: 'about',          icon: <PawPrint size={17} className="mr-1.5" /> },
    { name: 'Pet Boarding',  id: 'petshop',        icon: <Store size={17} className="mr-1.5" /> },
    { name: 'Emergency',     id: 'emergency',      icon: <Stethoscope size={17} className="mr-1.5" /> },
    { name: 'Reviews',       id: 'testimonials',   icon: null },
    { name: 'Join Us',       id: 'join',           icon: null },
    { name: 'FAQ',           id: 'faq',            icon: null },
  ];

  const navBg = isScrolled
    ? 'bg-white/80 backdrop-blur-xl shadow-sm border-b border-gray-200/60'
    : 'bg-transparent';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">

          {/* Logo */}
          <button onClick={() => scrollTo('home')} className="flex items-center flex-shrink-0">
            <PawPrint className="text-brand-orange mr-2" size={26} />
            <span className="text-xl font-extrabold text-brand-dark tracking-tight">
              Pet<span className="text-brand-teal">Cares</span>
            </span>
          </button>

          {/* Desktop nav — lg+ only */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className={`flex items-center px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200
                  ${activeSection === link.id
                    ? 'bg-brand-teal/10 text-brand-teal'
                    : 'text-gray-600 hover:text-brand-teal hover:bg-gray-100'
                  }`}
              >
                {link.icon}{link.name}
              </button>
            ))}
          </div>

          {/* SOS desktop button */}
          <div className="hidden lg:block">
            <button
              onClick={() => scrollTo('emergency')}
              className="inline-flex items-center gap-2 bg-emergency-red text-white px-5 py-2.5 rounded-full text-sm font-bold shadow-md shadow-red-200 hover:bg-red-700 transition-all animate-pulse"
            >
              <Stethoscope size={16} />
              SOS Vet
            </button>
          </div>

          {/* Hamburger — below lg */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition focus:outline-none"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile / Tablet slide-down drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
        } bg-white border-t border-gray-100 shadow-lg`}
      >
        <div className="px-4 py-4 space-y-1">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className={`flex items-center w-full px-4 py-3 rounded-xl text-base font-semibold transition-all
                ${activeSection === link.id
                  ? 'bg-brand-teal/10 text-brand-teal'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-brand-teal'
                }`}
            >
              {link.icon}{link.name}
            </button>
          ))}

          <div className="pt-2 pb-1">
            <button
              onClick={() => scrollTo('emergency')}
              className="flex items-center justify-center gap-2 w-full py-3.5 bg-emergency-red text-white rounded-xl font-bold text-base shadow-md shadow-red-100 hover:bg-red-700 transition"
            >
              <Stethoscope size={18} />
              SOS Emergency Vet
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
